import { Badge, Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import DataGridComponent from 'components/DataGrid'
import DatePicker from 'components/DatePicker'
import { PRESET_PERIODS } from 'components/DatePicker/constants'
import FilterButton from 'components/FilterButton'
import { useExportHistoryFilters } from 'hooks/useExportHistoryFilters'
import useGetExportHistoryList from 'queries/use-get-export-history'
import { useCallback, useMemo, useState } from 'react'
import { CustomTooltip } from '@hi/brabus'
import { getDateWithClientTimezone } from 'utils/date'
import { getTenantId, KEY } from 'utils/token/token'
import { renderChip } from 'utils/chip'
import { EXPORT_HISTORY_STATUS_VALUES } from 'constants/index'
import { DownloadOutlined, RefreshRounded } from '@mui/icons-material'
import { StyledEllipsisTypography } from 'components/Material/Typography'
import mixpanel from 'mixpanel-browser'
import { CustomNoRowsDataGrid } from './CustomizedDataGridComponents/CustomNoRowsDataGrid'
import FilterDrawer from './FilterDrawer'
import { DataGridSkeleton } from './CustomizedDataGridComponents'

function ExportHistoryContainer() {
  const tenantId = getTenantId()

  const { filters, setFilters, setPage } = useExportHistoryFilters()
  const [dates, setDates] = useState(() => {
    if (typeof filters.startDate === 'string') {
      const start = getDateWithClientTimezone(new Date(filters.startDate)).clientWithTimezone
      const end = getDateWithClientTimezone(new Date(filters.endDate)).clientWithTimezone
      return [new Date(start), new Date(end)]
    }
    return [filters.startDate, filters.endDate]
  })

  const { data, isPreviousData, isFetching, refetch } = useGetExportHistoryList({
    ...filters,
    tenantId,
  })
  const [showDrawer, setShowDrawer] = useState(false)

  const { rows = [], totalItems = 0 } = useMemo(() => {
    if (data) {
      return {
        rows: data.data.map((row, index) => ({
          id: row.id || index,
          ...row,
        })),
        totalItems: data.totalRecords,
      }
    }

    return {
      rows: [],
      totalItems: 0,
    }
  }, [data])

  const handleDateChange = useCallback(
    ([startDate, endDate]) => {
      setDates([startDate, endDate])

      setFilters({
        startDate: getDateWithClientTimezone(new Date(startDate)).clientWithTimezone,
        endDate: getDateWithClientTimezone(new Date(endDate)).clientWithTimezone,
      })
    },
    [setFilters],
  )

  const returnSubTitle = () => {
    if (data?.length) return `${data.length} exportação(ões) em exibição`
    return 'Nenhum registro de exportação em exibição'
  }

  const toggleDrawer = useCallback(() => {
    setShowDrawer(!showDrawer)
  }, [showDrawer])

  const findItemByField = (field, array) => {
    return array.find((item) => item.field === field)
  }

  const columns = [
    {
      field: 'requestDate',
      sortable: false,
      flex: 1,
      // maxWidth: 250,
      renderHeader: () => (
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            ':hover': {
              svg: {
                fill: '#00AEEF',
              },
            },
            bgcolor: 'none',
            gap: '0.25rem',
          }}
        >
          <StyledEllipsisTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
            Data de solicitação
          </StyledEllipsisTypography>
        </Box>
      ),
      renderCell: ({ row }) => {
        if (row.requestDate) {
          return (
            <Stack flexDirection="column" sx={{ width: '100%' }}>
              <CustomTooltip
                body={`${getDateWithClientTimezone(new Date(`${row.requestDate}Z`))
                  .clientDateString.split('-')
                  .reverse()
                  .join('/')} - ${getDateWithClientTimezone(new Date(`${row.requestDate}Z`)).clientTimeString}`}
              >
                <StyledEllipsisTypography>
                  {getDateWithClientTimezone(new Date(`${row.requestDate}Z`))
                    .clientDateString.split('-')
                    .reverse()
                    .join('/')}
                  {' - '}
                  {getDateWithClientTimezone(new Date(`${row.requestDate}Z`)).clientTimeString}
                </StyledEllipsisTypography>
              </CustomTooltip>
            </Stack>
          )
        }
        return (
          <StyledEllipsisTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
            Indisponível
          </StyledEllipsisTypography>
        )
      },
    },
    {
      field: 'origin',
      sortable: false,
      flex: 1,
      width: 120,
      renderHeader: () => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            bgcolor: 'none',
            gap: '0.25rem',
          }}
        >
          <StyledEllipsisTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
            Relatório de origem
          </StyledEllipsisTypography>
        </Box>
      ),
      renderCell: () => (
        <Stack flexDirection="column" sx={{ width: '100%' }}>
          <CustomTooltip body="Volume de disparos (HSM)">
            <StyledEllipsisTypography>Volume de disparos (HSM)</StyledEllipsisTypography>
          </CustomTooltip>
        </Stack>
      ),
    },
    {
      field: 'status',
      sortable: false,
      flex: 1,
      maxWidth: 180,
      renderHeader: () => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            bgcolor: 'none',
            gap: '0.25rem',
          }}
        >
          <StyledEllipsisTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
            Status
          </StyledEllipsisTypography>
        </Box>
      ),
      renderCell: ({ row }) => (
        <Box flex={1} display="flex" alignItems="center">
          {renderChip(EXPORT_HISTORY_STATUS_VALUES[row.status])}
        </Box>
      ),
    },
    {
      field: 'period',
      sortable: false,
      flex: 1,
      // maxWidth: 250,
      renderHeader: () => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            bgcolor: 'none',
            gap: '0.25rem',
          }}
        >
          <StyledEllipsisTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
            Período exportado
          </StyledEllipsisTypography>
        </Box>
      ),
      renderCell: ({ row }) => {
        return (
          <Stack flexDirection="column" sx={{ width: '100%' }}>
            <CustomTooltip
              body={
                <StyledEllipsisTypography as="span">
                  {getDateWithClientTimezone(new Date(findItemByField('startDate', row.filters).value))
                    .clientDateString.split('-')
                    .reverse()
                    .join('/')}{' '}
                  -{' '}
                  {getDateWithClientTimezone(new Date(findItemByField('endDate', row.filters).value))
                    .clientDateString.split('-')
                    .reverse()
                    .join('/')}
                </StyledEllipsisTypography>
              }
            >
              <StyledEllipsisTypography>
                {getDateWithClientTimezone(new Date(findItemByField('startDate', row.filters).value))
                  .clientDateString.split('-')
                  .reverse()
                  .join('/')}{' '}
                -{' '}
                {getDateWithClientTimezone(new Date(findItemByField('endDate', row.filters).value))
                  .clientDateString.split('-')
                  .reverse()
                  .join('/')}
              </StyledEllipsisTypography>
            </CustomTooltip>
          </Stack>
        )
      },
    },
    {
      field: 'actions',
      sortable: false,
      headerName: '',
      width: 140,
      renderHeader: () => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            bgcolor: 'none',
            gap: '0.25rem',
          }}
        >
          <StyledEllipsisTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
            Ações
          </StyledEllipsisTypography>
        </Box>
      ),
      renderCell: ({ row }) => (
        <Box flex={1} display="flex" align="center" justifyContent="left">
          <Button
            sx={{
              color: '#00AEEF',
              ':hover': {
                color: '#00AEEF',
                borderColor: '#00AEEF',
              },
            }}
            variant="outlined"
            disabled={!row?.fileUrl}
            onClick={() => {
              try {
                window.open(row.fileUrl)
                mixpanel.track('Download - Relatório de HSM', {
                  page: 'Histórico de exportações',
                  status: 'Success',
                })
              } catch (error) {
                mixpanel.track('Download - Relatório de HSM', {
                  page: 'Histórico de exportações',
                  status: 'Error',
                  message: error?.message || 'Erro ao abrir link para download',
                })
              }
            }}
            startIcon={<DownloadOutlined />}
          >
            Baixar
          </Button>
        </Box>
      ),
    },
  ]

  const FILTERED_PERIODS = PRESET_PERIODS.filter((item) => {
    if (!item.label.toLocaleLowerCase().includes('máximo') && !item.label.toLocaleLowerCase().includes('ontem'))
      return true
    return false
  })

  const onPaginationModelChange = useCallback(
    (model) => {
      if (isPreviousData) return
      setPage({ page: model.current + 1, pageSize: model.size })
    },
    [isPreviousData, setPage],
  )

  const badgeColor = filters?.status?.length || filters?.startExportPeriod ? 'error' : 'transparent'

  const slots = useMemo(
    () => ({
      noRowsOverlay: CustomNoRowsDataGrid,
      loadingOverlay: DataGridSkeleton,
    }),
    [],
  )

  const pagination = useMemo(
    () => ({
      page: parseInt(filters.page, 10) - 1,
      pageSize: parseInt(filters.pageSize, 10),
      pageSizeOptions: [5, 10, 20, 50, 100],
      paginationMode: 'server',
      onPaginationModelChange,
    }),
    [filters.page, filters.pageSize, onPaginationModelChange],
  )

  return (
    <>
      {showDrawer && <FilterDrawer open={showDrawer} toggleDrawer={toggleDrawer} />}

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <DatePicker onChange={handleDateChange} value={dates} presetPeriods={FILTERED_PERIODS} disabled={isFetching} />
        <Stack direction="row" gap={2}>
          <Badge variant="dot" badgeContent=" " color={badgeColor}>
            <FilterButton handleClick={toggleDrawer} loading={isFetching} />
          </Badge>
          <Tooltip title="Atualizar tabela" placement="top">
            <IconButton
              role="progressbar"
              onClick={() => {
                if (!isFetching) refetch()
              }}
              size="medium"
              sx={{
                color: '#00AEEF',
              }}
            >
              {isFetching ? (
                <CircularProgress
                  size={23}
                  sx={{
                    color: '#00AEEF',
                  }}
                />
              ) : (
                <RefreshRounded />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Stack
        display="flex"
        mt="1rem"
        flexDirection="column"
        gap="1rem"
        borderRadius=".25rem"
        padding="1rem"
        sx={{
          border: '1px solid #C4C4C4',
          borderRadius: '.25rem',
        }}
      >
        <Stack direction="row" gap=".5rem">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5.05039 22.375L3.65039 20.95L6.60039 18H4.35039V16H10.0004V21.65H8.00039V19.425L5.05039 22.375ZM12.0004 22V14H4.00039V4C4.00039 3.45 4.19622 2.97917 4.58789 2.5875C4.97956 2.19583 5.45039 2 6.00039 2H14.0004L20.0004 8V20C20.0004 20.55 19.8046 21.0208 19.4129 21.4125C19.0212 21.8042 18.5504 22 18.0004 22H12.0004ZM13.0004 9H18.0004L13.0004 4V9Z"
              fill="#757575"
            />
          </svg>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '.5rem',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#666',
                fontSize: '1.5rem',
                fontWeight: 700,
                lineHeight: '1.5rem',
              }}
            >
              Todas as exportações
            </Typography>
            <Typography variant="body1" color="GrayText">
              {returnSubTitle()}
            </Typography>
          </Box>
        </Stack>
        <DataGridComponent
          rows={isFetching ? [] : rows}
          rowCount={isFetching ? 0 : totalItems}
          columns={columns}
          loading={isFetching}
          slots={slots}
          pagination={pagination}
          sorting={{
            sortModel: [{ field: 'requestDate', sort: 'desc' }],
          }}
          getRowId={(el) => el.id}
          rowHeight={63}
          hideFooterSelectedRowCount
          disableRowSelectionOnClick
          disableColumnMenu
        />
      </Stack>
    </>
  )
}

export default ExportHistoryContainer
